<svelte:options customElement="hb-dialog" />

<script lang="ts">
  import { fade, fly } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import type { Component, Events } from "./types/webcomponent.type";

  let {
    id = "",
    // style = "",
    content = "",
    show = "no",
    title = "",
    backdrop = true,
    keyboard = true,
    describedby = "",
    labelledby = "",
    closelabel = "",
    confirmlabel = "",
    disable_confirm = false,
    confirm_btn_class = "primary",
    close_btn_class = "secondary",
    hide_close = false, // When true, hides the close control
    hide_confirm = false, // When true, hides the confirm button
  }: Component = $props();

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  const onOpened = () => dispatch("modalShow", { id, show: true });
  const onClosed = () => dispatch("modalShow", { id, show: false });
  let _keyboardEvent: any;
  function attachEvent(target: any, ...args: any) {
    target.addEventListener(...args);
    return {
      remove: () => target.removeEventListener(...args),
    };
  }
  function handleBackdrop(event: MouseEvent) {
    if (backdrop && event.currentTarget === event.target) {
      event.stopPropagation();
      show = "no";
    }
  }

  /** Maps legacy Bootstrap-style suffixes (e.g. `primary`, `btn-primary`) to Bulma `is-*` modifiers. */
  function dialogButtonModifier(kind: string | undefined): string {
    const raw = (kind ?? "").trim().toLowerCase().replace(/^btn-/, "");
    const map: Record<string, string> = {
      primary: "is-primary",
      secondary: "is-light",
      success: "is-success",
      danger: "is-danger",
      warning: "is-warning",
      info: "is-info",
      light: "is-light",
      dark: "is-dark",
      link: "is-ghost",
    };
    return map[raw] || "is-light";
  }
  function onModalOpened() {
    if (keyboard) {
      _keyboardEvent = attachEvent(document, "keydown", (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          show = "no";
        }
      });
    }
    onOpened();
  }
  function onModalClosed() {
    if (_keyboardEvent) {
      _keyboardEvent.remove();
    }
    onClosed();
  }
  $effect(() => {
    if (typeof disable_confirm === "string")
      disable_confirm =
        (disable_confirm as string).toLowerCase() === "true" ||
        (disable_confirm as string).toLowerCase() === "yes";
    if (
      typeof hide_close === "string" &&
      ((hide_close as string).toLowerCase() === "true" ||
        (hide_close as string).toLowerCase() === "yes")
    ) {
      hide_close = true;
    }
    if (
      typeof hide_confirm === "string" &&
      ((hide_confirm as string).toLowerCase() === "true" ||
        (hide_confirm as string).toLowerCase() === "yes")
    ) {
      hide_confirm = true;
    }
    if (show === "yes") {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  });

  function handleConfirm() {
    dispatch("modalConfirm", { id, confirm: true });
    show = "no";
  }
  function handleCancel() {
    show = "no";

    dispatch("modalConfirm", { id, confirm: false });
  }
</script>

{#if show === "yes"}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    id="modal-{id}"
    class="modal is-active"
    tabindex="-1"
    role="dialog"
    aria-labelledby={labelledby}
    aria-describedby={describedby}
    aria-modal="true"
    onintroend={onModalOpened}
    onoutroend={onModalClosed}
    transition:fade
  >
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div
      class="modal-background"
      role="presentation"
      onclick={handleBackdrop}
      transition:fade={{ duration: 150 }}
    ></div>
    <div
      class="modal-card"
      part="modal-dialog"
      role="document"
      in:fly={{ y: -50, duration: 300 }}
      out:fly={{ y: -50, duration: 300, easing: quintOut }}
    >
      <header class="modal-card-head">
        <slot name="header">
          <div class="modal-card-title">
            <slot name="title">{title || "title"}</slot>
          </div>
          {#if !hide_close}
            <button
              type="button"
              class="delete"
              aria-label="Close"
              onclick={() => (show = "no")}
            ></button>
          {/if}
        </slot>
      </header>
      <section class="modal-card-body content hb-dialog-body">
        <slot name="body-content"
          >{content || "Woohoo, you're reading this text in a modal!"}</slot
        >
      </section>
      <footer class="modal-card-foot">
        <slot name="modal-footer">
          <slot name="footer">
            <div class="buttons">
              {#if !hide_close}
                <button
                  type="button"
                  class="button {dialogButtonModifier(close_btn_class)}"
                  onclick={() => handleCancel()}
                  ><slot name="close-button-label">{closelabel || "Close"}</slot
                  ></button
                >
              {/if}
              {#if !hide_confirm}
                <button
                  disabled={disable_confirm ? true : false}
                  type="button"
                  class="button {dialogButtonModifier(confirm_btn_class)}"
                  onclick={() => handleConfirm()}
                  ><slot name="confirm-button-label"
                    >{confirmlabel || "Save changes"}</slot
                  >
                </button>
              {/if}
            </div>
          </slot>
        </slot>
      </footer>
    </div>
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
